'use client';

import { useEffect, useState } from 'react';

import { getLogger } from '@/logger/logger';
import { HttpException } from '@/utils/api/api-client';

export type EntityProfileStatus = 'loading' | 'ready' | 'error';

export interface UseEntityProfileOptions<TProfile> {
  fetchProfile: (slug: string) => Promise<TProfile>;
  fallbackProfile: () => TProfile;
  loggerContext: string;
  enabled?: boolean;
}

export interface UseEntityProfileReturn<TProfile> {
  profile: TProfile | null;
  status: EntityProfileStatus;
  error: unknown;
}

export function isProfileNotFound(error: unknown): boolean {
  return error instanceof HttpException && error.status === 404;
}

export function useEntityProfile<TProfile>(
  slug: string,
  { fetchProfile, fallbackProfile, loggerContext, enabled = true }: UseEntityProfileOptions<TProfile>,
): UseEntityProfileReturn<TProfile> {
  const [profile, setProfile] = useState<TProfile | null>(null);
  const [status, setStatus] = useState<EntityProfileStatus>('loading');
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;

    setProfile(null);
    setStatus('loading');
    setError(null);

    fetchProfile(slug)
      .then((result) => {
        if (cancelled) {
          return;
        }

        setProfile(result);
        setStatus('ready');
      })
      .catch((error) => {
        getLogger(loggerContext).error('Failed to fetch the entity profile', {
          entity: { event: 'profile_failed', slug },
          error,
        });

        if (cancelled) {
          return;
        }

        setError(error);
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, enabled]);

  if (!enabled) {
    return { profile: fallbackProfile(), status: 'ready', error: null };
  }

  if (status !== 'ready') {
    return { profile: fallbackProfile(), status, error };
  }

  return { profile, status, error };
}
